/**
 * @file     createDesignTheme.ts
 * @readme   ./README.md
 * @purpose  Factory function that assembles a fully typed AppTheme object from all design tokens for a given light/dark mode with optional overrides.
 */
import type { AppTheme, ThemeMode } from '@/core/types';
import type { ColorTokens } from '@/core/types';
import {
  breakpointTokens,
  colorPalettesByMode,
  createRawSemanticColors,
  createSemanticColorTokens,
  layoutTokens,
  motionTokens,
  radiusTokens,
  spacingTokens,
  typographyTokens
} from '@/design-system/tokens';

/** Build a ColorTokens object from semantic colors and palette values. */
const buildColorTokens = (
  sc: AppTheme['semanticColors'],
  palette: AppTheme['palette'],
  mode: ThemeMode,
  overrides?: Partial<ColorTokens>,
  raw?: boolean,
): ColorTokens => ({
  primary: sc.interactivePrimary,
  primaryMuted: sc.backgroundSecondary,
  secondary: sc.interactiveSecondary,
  background: sc.backgroundPrimary,
  surface: sc.surfaceCard,
  surfaceMuted: sc.surfaceElevated,
  textPrimary: sc.textPrimary,
  textSecondary: sc.textSecondary,
  textMuted: sc.textMuted,
  textOnPrimary: sc.textOnPrimary,
  border: sc.borderSubtle,
  divider: sc.divider,
  success: sc.statusSuccess,
  warning: sc.statusWarning,
  error: sc.statusError,
  info: sc.statusInfo,
  hoverSurface: raw ? palette.interaction.hoverSurface : 'var(--r-hover-surface)',
  hoverElevated: raw ? palette.interaction.hoverElevated : 'var(--r-hover-elevated)',
  pressedSurface: raw ? palette.interaction.pressedSurface : 'var(--r-pressed-surface)',
  focusRing: sc.interactiveFocusRing,
  accentYellow: raw ? palette.interaction.accentYellow : 'var(--r-accent-yellow)',
  accentYellowMuted: raw ? palette.interaction.accentYellowMuted : 'var(--r-accent-yellow-muted)',
  ...(overrides ?? {}),
});

export const createDesignTheme = (
  mode: ThemeMode,
  overrides?: Partial<Omit<AppTheme, 'mode'>>
): AppTheme => {
  const palette = overrides?.palette ?? colorPalettesByMode[mode];
  const semanticColors = {
    ...createSemanticColorTokens(mode, palette),
    ...(overrides?.semanticColors ?? {})
  };
  const rawSemanticColors = {
    ...createRawSemanticColors(mode, palette),
    ...(overrides?.rawSemanticColors ?? {})
  };

  return {
    mode,
    palette,
    semanticColors,
    rawSemanticColors,
    colors: buildColorTokens(semanticColors, palette, mode, overrides?.colors, false),
    rawColors: buildColorTokens(rawSemanticColors, palette, mode, overrides?.colors, true),
    spacing: [...spacingTokens],
    typography: {
      ...typographyTokens,
      ...(overrides?.typography ?? {})
    },
    radius: {
      ...radiusTokens,
      ...(overrides?.radius ?? {})
    },
    breakpoints: {
      ...breakpointTokens,
      ...(overrides?.breakpoints ?? {})
    },
    motion: {
      ...motionTokens,
      ...(overrides?.motion ?? {})
    },
    layout: {
      ...layoutTokens,
      ...(overrides?.layout ?? {})
    }
  };
};
